/**
 * Channels resource — dashboard-side per-org channel config summaries
 * (ARCHITECTURE §2 `channels.py`, §5). Secrets stay on the backend; only
 * redacted {@link ChannelIntegrationSummary} data crosses the wire.
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type { ChannelConnectInput, ChannelIntegrationSummary, MetaWebhookInfo } from '../../types';

/** Partial update to a channel integration (enable/disable, settings, credential rotation). */
export interface ChannelUpdateInput {
  enabled?: boolean;
  settings?: Record<string, unknown>;
  credentials?: Record<string, string>;
}

export class ChannelsResource {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ChannelIntegrationSummary[]> {
    return this.http.get(endpoints.channels.list());
  }

  get(channelId: string): Promise<ChannelIntegrationSummary> {
    return this.http.get(endpoints.channels.detail(channelId));
  }

  /** Connect/configure a channel (upsert by channel). Credentials are write-only. */
  connect(body: ChannelConnectInput): Promise<ChannelIntegrationSummary> {
    return this.http.post(endpoints.channels.list(), body);
  }

  /** Patch an integration — toggle enabled, edit settings, or rotate credentials. */
  update(channelId: string, patch: ChannelUpdateInput): Promise<ChannelIntegrationSummary> {
    return this.http.patch(endpoints.channels.detail(channelId), patch);
  }

  /** Disconnect (delete) an integration. */
  remove(channelId: string): Promise<void> {
    return this.http.delete(endpoints.channels.detail(channelId));
  }

  /** Meta webhook callback URL + verify token to configure in the Meta app dashboard. */
  metaWebhookInfo(): Promise<MetaWebhookInfo> {
    return this.http.get(endpoints.channels.metaWebhookInfo());
  }
}
